/**
 * ODM-aware XML diff utility
 * Port of Python odm_comparator.py for element-level XML comparison
 */

export interface ElementDiff {
  path: string           // XPath-like path to element
  elementType: string    // e.g., 'ItemGroupDef', 'ItemDef'
  oid: string | null     // OID attribute value if present
  changeType: 'added' | 'removed' | 'modified'
  details?: AttributeDiff[]
  lineStart?: number     // Line number in formatted XML (legacy, for navigation)
  lineEnd?: number
  leftLineStart?: number  // Line number in the LEFT (original) XML
  leftLineEnd?: number    // Closing tag line in the LEFT XML
  rightLineStart?: number // Line number in the RIGHT (modified) XML
  rightLineEnd?: number   // Closing tag line in the RIGHT XML
}

export interface AttributeDiff {
  attribute: string
  oldValue: string | null
  newValue: string | null
}

// ODM elements that use OID for identification
const OID_ELEMENTS = new Set([
  'Study', 'MetaDataVersion', 'Protocol', 'StudyEventDef', 'FormDef',
  'ItemGroupDef', 'ItemDef', 'CodeList', 'MethodDef', 'ConditionDef',
  'StudyEventRef', 'FormRef', 'ItemGroupRef', 'ItemRef', 'CodeListRef'
])

// Elements that use other identifying attributes
const ID_ATTRIBUTES: Record<string, string[]> = {
  'StudyEventRef': ['StudyEventOID'],
  'FormRef': ['FormOID'],
  'ItemGroupRef': ['ItemGroupOID'],
  'ItemRef': ['ItemOID'],
  'CodeListRef': ['CodeListOID'],
  'TranslatedText': ['xml:lang'],
  'Alias': ['Context', 'Name'],
}

/**
 * Create a unique key for an ODM element based on its type and identifying attributes
 */
function createElementKey(element: Element): string {
  const tag = element.tagName
  const parts = [tag]

  // Check for OID attribute first
  if (OID_ELEMENTS.has(tag) && element.hasAttribute('OID')) {
    parts.push(`OID=${element.getAttribute('OID')}`)
  }

  // Check for other identifying attributes
  const idAttrs = ID_ATTRIBUTES[tag]
  if (idAttrs) {
    for (const attr of idAttrs) {
      if (element.hasAttribute(attr)) {
        parts.push(`${attr}=${element.getAttribute(attr)}`)
      }
    }
  }

  return parts.join('|')
}

/**
 * Build a map of element keys to elements for quick lookup
 */
function buildElementMap(root: Element): Map<string, Element> {
  const map = new Map<string, Element>()

  function traverse(element: Element, parentPath: string = '') {
    const key = createElementKey(element)
    const fullKey = parentPath ? `${parentPath}/${key}` : key
    map.set(fullKey, element)

    // Traverse children
    for (let i = 0; i < element.children.length; i++) {
      traverse(element.children[i], fullKey)
    }
  }

  traverse(root)
  return map
}

/**
 * Compare attributes between two elements
 */
function compareAttributes(oldEl: Element, newEl: Element): AttributeDiff[] {
  const diffs: AttributeDiff[] = []
  const oldAttrs = new Set(oldEl.getAttributeNames())
  const newAttrs = new Set(newEl.getAttributeNames())

  // Check for removed or modified attributes
  for (const attr of oldAttrs) {
    const oldVal = oldEl.getAttribute(attr)
    if (!newAttrs.has(attr)) {
      diffs.push({ attribute: attr, oldValue: oldVal, newValue: null })
    } else {
      const newVal = newEl.getAttribute(attr)
      if (oldVal !== newVal) {
        diffs.push({ attribute: attr, oldValue: oldVal, newValue: newVal })
      }
    }
  }

  // Check for added attributes
  for (const attr of newAttrs) {
    if (!oldAttrs.has(attr)) {
      diffs.push({ attribute: attr, oldValue: null, newValue: newEl.getAttribute(attr) })
    }
  }

  return diffs
}

/**
 * Get text content of an element (excluding children)
 */
function getDirectTextContent(element: Element): string {
  let text = ''
  for (let i = 0; i < element.childNodes.length; i++) {
    const node = element.childNodes[i]
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent || ''
    }
  }
  return text.trim()
}

/**
 * Compare two ODM XML documents and return element-level diffs
 */
export function compareOdmXml(oldXml: string, newXml: string): ElementDiff[] {
  // Guard against empty/missing XML
  if (!oldXml?.trim() || !newXml?.trim()) {
    return []
  }

  const parser = new DOMParser()
  const oldDoc = parser.parseFromString(oldXml, 'application/xml')
  const newDoc = parser.parseFromString(newXml, 'application/xml')

  // Check for parse errors (common with fragments or truncated XML — not a bug)
  const oldError = oldDoc.querySelector('parsererror')
  const newError = newDoc.querySelector('parsererror')
  if (oldError || newError) {
    console.debug('ODM diff: skipping — XML not parseable (likely a fragment)')
    return []
  }

  const oldRoot = oldDoc.documentElement
  const newRoot = newDoc.documentElement

  const oldMap = buildElementMap(oldRoot)
  const newMap = buildElementMap(newRoot)

  const diffs: ElementDiff[] = []

  // Find removed and modified elements
  for (const [key, oldEl] of oldMap) {
    const newEl = newMap.get(key)
    if (!newEl) {
      // Element was removed
      diffs.push({
        path: key,
        elementType: oldEl.tagName,
        oid: oldEl.getAttribute('OID'),
        changeType: 'removed',
      })
    } else {
      // Element exists in both - check for modifications
      const attrDiffs = compareAttributes(oldEl, newEl)
      const oldText = getDirectTextContent(oldEl)
      const newText = getDirectTextContent(newEl)

      if (attrDiffs.length > 0 || oldText !== newText) {
        diffs.push({
          path: key,
          elementType: oldEl.tagName,
          oid: oldEl.getAttribute('OID'),
          changeType: 'modified',
          details: attrDiffs,
        })
      }
    }
  }

  // Find added elements
  for (const [key, newEl] of newMap) {
    if (!oldMap.has(key)) {
      diffs.push({
        path: key,
        elementType: newEl.tagName,
        oid: newEl.getAttribute('OID'),
        changeType: 'added',
      })
    }
  }

  return diffs
}

/**
 * Search for an element's opening tag in a set of lines.
 * Returns 1-based line number, or 0 if not found.
 */
function findElementLine(
  lines: string[],
  elementType: string,
  oid: string | null,
  identifyingAttr: string
): number {
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (!line.includes(`<${elementType}`)) continue

    if (elementType === 'ItemRef' && identifyingAttr) {
      const itemOidMatch = identifyingAttr.match(/ItemOID=([^|]+)/)
      if (itemOidMatch && line.includes(`ItemOID="${itemOidMatch[1]}"`)) {
        return i + 1
      }
    } else if (oid && line.includes(`OID="${oid}"`)) {
      return i + 1
    } else if (elementType === 'TranslatedText') {
      const langMatch = identifyingAttr.match(/xml:lang=([^|]+)/)
      if (langMatch && line.includes(`xml:lang="${langMatch[1]}"`)) {
        return i + 1
      }
      return i + 1
    } else if (!oid) {
      return i + 1
    }
  }
  return 0
}

/**
 * Find the closing tag line for an element starting at `startLine`.
 * Handles self-closing tags (returns startLine) and nested elements.
 */
function findElementEndLine(lines: string[], startLine: number, elementType: string): number {
  if (startLine <= 0 || startLine > lines.length) return startLine

  const openLine = lines[startLine - 1]
  // Self-closing tag
  if (openLine.includes('/>')) return startLine

  // Search for matching closing tag, tracking nesting depth
  let depth = 1
  for (let i = startLine; i < lines.length; i++) {
    const line = lines[i]
    // Count opening tags of this type (excluding self-closing)
    if (line.includes(`<${elementType}`) && !line.includes('/>')) {
      depth++
    }
    if (line.includes(`</${elementType}>`)) {
      depth--
      if (depth === 0) return i + 1 // 1-based
    }
  }
  return startLine // Fallback: treat as single line
}

/**
 * Find line numbers for diff elements in BOTH formatted XMLs.
 * Populates leftLineStart, rightLineStart, and legacy lineStart.
 */
export function findDiffLineNumbers(
  diffs: ElementDiff[],
  formattedLeft: string,
  formattedRight: string
): ElementDiff[] {
  const leftLines = formattedLeft.split('\n')
  const rightLines = formattedRight.split('\n')

  return diffs.map(diff => {
    const pathParts = diff.path.split('/')
    const lastPart = pathParts[pathParts.length - 1]
    const identifyingAttr = lastPart.split('|').slice(1).join('|')

    const leftLine = findElementLine(leftLines, diff.elementType, diff.oid, identifyingAttr)
    const rightLine = findElementLine(rightLines, diff.elementType, diff.oid, identifyingAttr)

    const lineStart = diff.changeType === 'removed'
      ? (leftLine || 1)
      : (rightLine || 1)

    const leftEnd = leftLine ? findElementEndLine(leftLines, leftLine, diff.elementType) : undefined
    const rightEnd = rightLine ? findElementEndLine(rightLines, rightLine, diff.elementType) : undefined

    return {
      ...diff,
      lineStart,
      leftLineStart: leftLine || undefined,
      leftLineEnd: leftEnd,
      rightLineStart: rightLine || undefined,
      rightLineEnd: rightEnd,
    }
  })
}

/**
 * Generate a human-readable summary of changes
 */
export function summarizeChanges(diffs: ElementDiff[]): string {
  if (diffs.length === 0) return 'No changes detected'

  const added = diffs.filter(d => d.changeType === 'added')
  const removed = diffs.filter(d => d.changeType === 'removed')
  const modified = diffs.filter(d => d.changeType === 'modified')

  const parts: string[] = []

  if (added.length > 0) {
    const types = [...new Set(added.map(d => d.elementType))]
    parts.push(`Added: ${added.length} element(s) (${types.join(', ')})`)
  }

  if (removed.length > 0) {
    const types = [...new Set(removed.map(d => d.elementType))]
    parts.push(`Removed: ${removed.length} element(s) (${types.join(', ')})`)
  }

  if (modified.length > 0) {
    const types = [...new Set(modified.map(d => d.elementType))]
    parts.push(`Modified: ${modified.length} element(s) (${types.join(', ')})`)
  }

  return parts.join('; ')
}

/**
 * Get a concise description of a single diff
 */
export function describeDiff(diff: ElementDiff): string {
  const oidPart = diff.oid ? ` (OID: ${diff.oid})` : ''

  switch (diff.changeType) {
    case 'added':
      return `Added ${diff.elementType}${oidPart}`
    case 'removed':
      return `Removed ${diff.elementType}${oidPart}`
    case 'modified':
      if (diff.details && diff.details.length > 0) {
        const attrChanges = diff.details.map(d => {
          if (d.oldValue === null) return `+${d.attribute}`
          if (d.newValue === null) return `-${d.attribute}`
          return `${d.attribute}: "${d.oldValue}" → "${d.newValue}"`
        }).join(', ')
        return `Modified ${diff.elementType}${oidPart}: ${attrChanges}`
      }
      return `Modified ${diff.elementType}${oidPart}`
    default:
      return `Changed ${diff.elementType}${oidPart}`
  }
}
